import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Award,
  Calendar,
  ClipboardList,
  Crosshair,
  GraduationCap,
  Home,
  Palette,
  Sparkles,
  Target,
  UserCheck,
  Users2,
  Wallet,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import {
  Bot,
  Contacts,
  Dashboard2,
  DocTick,
  HotAirBalloon,
  UserCircle,
} from "@/components/icons";
import { MobileChatHistory } from "@/features/copilot/components/MobileChatHistory";
import { useMobileChatHistory } from "@/features/copilot/context/MobileChatHistoryContext";
import { canAccessLeadFinder } from "@/features/leads/utils/access";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getUserRole } from "@/utils/users";

import { DesktopSidebar } from "./navbar/DesktopSidebar";
import { MobileHeader } from "./navbar/MobileHeader";
import { MobileMenu } from "./navbar/MobileMenu";
import { NavItem } from "./navbar/types";
import { useSidebar } from "./useSidebarContext";

const NavBar = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { collapsed, setCollapsed } = useSidebar();
  const { logout, user, loginWithRedirect } = useAuth0();
  const { user: currentUser } = useCurrentUser();

  // Mobile chat history context
  const chatHistory = useMobileChatHistory();
  const isOnChatPage = location.pathname === "/chat" || location.pathname === "/co-pilot";

  const userRole = getUserRole(user);

  // If no user role, redirect to login and don't render anything
  useEffect(() => {
    if (userRole === null) {
      loginWithRedirect();
    }
  }, [userRole, loginWithRedirect]);

  // Handle body overflow for both menus - use scrollbar-gutter to prevent layout shift
  useEffect(() => {
    const isAnyMenuOpen = mobileMenuOpen || chatHistory.isOpen;
    if (isAnyMenuOpen && isMobile) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
    };
  }, [mobileMenuOpen, chatHistory.isOpen, isMobile]);

  // Don't render anything if user role is null
  if (userRole === null) {
    return null;
  }

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + "/") ||
      location.pathname.startsWith(path + "?")
    );
  };

  // For non-subscribed licensees, show full menu but with disabled items
  const isNonSubscribedLicensee =
    userRole === "licensee" && currentUser && !currentUser.subscriptionActive;

  const allNavItems: NavItem[] = [
    {
      name: "Dashboard",
      path: "/",
      icon: <Dashboard2 className="h-6 w-6" />,
      roles: ["licensee", "mentor"],
    },
    // Chat only available for non-legacy founders
    ...(currentUser?.nonAIFounder !== true
      ? [
          {
            name: "Chat",
            path: "/chat",
            icon: <Bot className="h-6 w-6" />,
            roles: ["licensee"],
            badge: "Beta",
          },
          {
            name: "Agents",
            path: "/stacey-library",
            icon: <Sparkles className="h-6 w-6" />,
            roles: ["licensee"],
          },
        ]
      : []),
    {
      name: "Invoices",
      path: "/invoices",
      icon: <DocTick className="h-6 w-6" />,
      roles: ["licensee"],
    },
    {
      name: "Clients",
      path: "/clients",
      icon: <Contacts className="h-6 w-6" />,
      roles: ["licensee"],
    },
    // Lead Finder — gated by feature flag in production
    ...(canAccessLeadFinder(currentUser)
      ? [
          {
            name: "Lead Finder",
            path: "/leads",
            icon: <Crosshair className="h-6 w-6" />,
            roles: ["licensee"],
            badge: "Beta",
          },
        ]
      : []),
    // Profile only available for certified founders
    ...(currentUser?.level &&
    ["certified", "pro", "x"].includes(currentUser.level.toLowerCase())
      ? [
          {
            name: "Profile",
            path: "/founder/profile",
            icon: <UserCircle className="h-6 w-6" />,
            roles: ["licensee"] as (
              | "licensee"
              | "mentor"
              | "admin"
              | "business"
            )[],
          },
        ]
      : []),
    {
      name: "Community & learning",
      path: "https://launch.stack.expert",
      icon: <HotAirBalloon className="h-6 w-6" />,
      roles: ["licensee"],
      external: true,
    },
    {
      name: "Founders",
      path: "/my-founders",
      icon: <Contacts className="h-6 w-6" />,
      roles: ["mentor"],
    },
    {
      name: "Profile",
      path: "/mentor/profile",
      icon: <UserCircle className="h-6 w-6" />,
      roles: ["mentor"],
    },
    {
      name: "Founders",
      path: "/founders",
      icon: <Users2 className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Applicants",
      path: "/applicants",
      icon: <ClipboardList className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Mentors",
      path: "/mentors",
      icon: <GraduationCap className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Cohorts",
      path: "/cohorts",
      icon: <Calendar className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Admins",
      path: "/admins",
      icon: <UserCheck className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Certified Push",
      path: "/certified-push",
      icon: <Target className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Certified Founders",
      path: "/certified-founders",
      icon: <Award className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Payouts",
      path: "/payouts",
      icon: <Wallet className="h-6 w-6" />,
      roles: ["admin"],
    },
    {
      name: "Design System",
      path: "/design-system",
      icon: <Palette className="h-6 w-6" />,
      roles: ["admin"],
    },
  ];

  // Filter navigation items based on user role and subscription status
  const navItems = isNonSubscribedLicensee
    ? [
        {
          name: "Welcome",
          path: "/welcome",
          icon: <Home className="h-6 w-6" />,
          roles: ["licensee"],
          disabled: false,
        },
        {
          name: "Dashboard",
          path: "/",
          icon: <Dashboard2 className="h-6 w-6" />,
          roles: ["licensee"],
          disabled: true,
        },
        {
          name: "Invoices",
          path: "/invoices",
          icon: <DocTick className="h-6 w-6" />,
          roles: ["licensee"],
          disabled: true,
        },
        {
          name: "Clients",
          path: "/clients",
          icon: <Contacts className="h-6 w-6" />,
          roles: ["licensee"],
          disabled: true,
        },
        ...(canAccessLeadFinder(currentUser)
          ? [
              {
                name: "Lead Finder",
                path: "/leads",
                icon: <Crosshair className="h-6 w-6" />,
                roles: ["licensee"],
                disabled: true,
              },
            ]
          : []),

        {
          name: "Community & learning",
          path: "https://launch.stack.expert",
          icon: <HotAirBalloon className="h-6 w-6" />,
          roles: ["licensee"],
          external: true,
          disabled: true,
        },
        // Chat only available for non-legacy founders
        ...(currentUser?.nonAIFounder !== true
          ? [
              {
                name: "Chat [Beta]",
                path: "/chat",
                icon: <Bot className="h-6 w-6" />,
                roles: ["licensee"],
                disabled: true,
              },
              {
                name: "Agents",
                path: "/stacey-library",
                icon: <Sparkles className="h-6 w-6" />,
                roles: ["licensee"],
                disabled: true,
              },
            ]
          : []),
      ]
    : allNavItems.filter((item) => item.roles.includes(userRole));

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const toggleSidebar = () => setCollapsed(!collapsed);

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
        federated: true,
      },
    });
  };

  return (
    <>
      <DesktopSidebar
        collapsed={collapsed}
        toggleSidebar={toggleSidebar}
        navItems={navItems}
        isActive={isActive}
        handleLogout={handleLogout}
      />
      <MobileHeader
        mobileMenuOpen={mobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        collapsed={collapsed}
        chatHistoryOpen={chatHistory.isOpen}
        toggleChatHistory={chatHistory.toggleOpen}
        showChatHistory={isOnChatPage && chatHistory.isRegistered}
      />
      <MobileMenu
        mobileMenuOpen={mobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        navItems={navItems}
        isActive={isActive}
        handleLogout={handleLogout}
        collapsed={collapsed}
      />
      {/* Mobile Chat History - full screen like burger menu */}
      {isOnChatPage && chatHistory.isRegistered && chatHistory.onThreadSelect && chatHistory.onThreadDelete && chatHistory.onNewThread && (
        <MobileChatHistory
          threads={chatHistory.threads}
          activeThreadId={chatHistory.activeThreadId}
          onThreadSelect={chatHistory.onThreadSelect}
          onThreadDelete={chatHistory.onThreadDelete}
          onNewThread={chatHistory.onNewThread}
          isLoading={chatHistory.isLoading}
          open={chatHistory.isOpen}
          onOpenChange={chatHistory.setIsOpen}
        />
      )}
    </>
  );
};

export default NavBar;
